import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const INDUSTRIES = [
    { label: "Gaming & Virtual Worlds", anchor: "gaming" },
    { label: "Companions & Assistants", anchor: "companions" },
    { label: "Humanoid Robotics", anchor: "robotics" },
    { label: "Customer Support & Research", anchor: "support-cx" },
    { label: "Hospitality & Rental Stays", anchor: "hospitality-stays" },
];

const PRODUCTS_B2C = [
    { label: "AI Companion", sub: "Adaptive chat that remembers you", href: "/products/companion", external: false },
    { label: "Emotion Profile Tracking", sub: "Your mood patterns over time", href: "/products/emotion-profile", external: false },
    { label: "Self-Help & Mood Coach", sub: "Guided emotional well-being", href: "/products/mood-coach", external: false },
    { label: "Text Humanizer", sub: "Un-AI articles & messages", href: "/products/text-humanizer", external: false },
    { label: "Voice Journal", sub: "Speak it, get insight back", href: "/products/voice-journal", external: false },
];

const PRODUCTS_B2B = [
    { label: "Emotion Models", sub: "Face · voice · text recognition", href: "/products/emotion-models", external: false },
    { label: "Multimodal Fusion", sub: "Face + voice + biosignal", href: "/products/fusion", external: false },
    { label: "Emotion API & SDK", sub: "REST + client SDKs", href: "https://developer.aihumanity.io", external: true },
    { label: "VoicePlay Engine", sub: "Game NPC character runtime", href: "https://voiceplay.aihumanity.io", external: true },
    { label: "AI Perception API", sub: "Heartbeat · Gaze · Calmness", href: "/perception", external: false },
    { label: "Custom Model Training", sub: "Fine-tune on your data", href: "/products/custom-training", external: false },
    { label: "Session Insights", sub: "Post-session emotion reports", href: "/products/session-insights", external: false },
];

const USE_CASES_B2C = [
    { label: "Reduce Loneliness", sub: "AI companionship that remembers you", href: "/use-cases/companionship", external: false },
    { label: "Build Mood Awareness", sub: "Spot patterns before they become a crisis", href: "/use-cases/mood-awareness", external: false },
    { label: "Write Like Yourself", sub: "Humanize AI drafts without losing your voice", href: "/use-cases/authentic-writing", external: false },
    { label: "Make Sense of Your Day", sub: "Speak it, get structured insight back", href: "/use-cases/daily-reflection", external: false },
];

const USE_CASES_B2B = [
    { label: "AI Front Desk For Homestays", sub: "WeChat-first post-booking guest support", href: "/use-cases/ai-front-desk-for-homestays", external: false },
    { label: "Reduce Support Escalations", sub: "Detect frustration before tickets escalate", href: "/use-cases/support-de-escalation", external: false },
    { label: "Turn Every Interaction Into Insight", sub: "Post-session emotion reports", href: "/use-cases/interaction-insights", external: false },
    { label: "Increase Player Retention", sub: "NPCs that react and remember", href: "/use-cases/player-retention", external: false },
    { label: "Reduce Onboarding Drop-off", sub: "Guide confused users automatically", href: "/use-cases/onboarding-drop-off", external: false },
    { label: "Improve Emotion Accuracy on Your Own Data", sub: "Custom-trained models, same API", href: "/use-cases/domain-tuned-models", external: false },
];

const SDK_LINKS = [
    { label: "Emotion SDK", sub: "For developers", href: "https://developer.aihumanity.io", external: true },
    { label: "VoicePlay", sub: "Game AI character", href: "https://voiceplay.aihumanity.io", external: true },
    { label: "AI Perception", sub: "Heartbeat · Gaze · Calmness", href: "/perception", external: false },
];

const DEMO_STARTERS = [
    { label: "All Demos", sub: "See the full demo overview page", href: "/demo", external: false },
    { label: "Build a Support Agent", sub: "Describe your business, test a live voice agent for it", href: "/demo/build-agent", external: false },
    { label: "Live Voice Agent", sub: "Talk to an emotion-aware agent in real time", href: "/demo/voice-agent", external: false },
    { label: "Voice Front Desk", sub: "See how a homestay AI front desk could work", href: "/demo/voice-host", external: false },
    { label: "Reads the Room", sub: "See tone and strategy shift with the same words", href: "/demo?tab=agent#demo-lab", external: false },
    { label: "Tone That Adapts", sub: "Compare a generic reply to an emotion-aware one", href: "/demo?tab=voice#demo-lab", external: false },
];

const DEMO_BY_PRODUCT = [
    { label: "Relationship Engine", sub: "Agent EQ scenarios", href: "/demo?tab=agent#demo-lab", external: false },
    { label: "Emotion API & SDK", sub: "Voice EQ scenarios", href: "/demo?tab=voice#demo-lab", external: false },
    { label: "Text Humanizer", sub: "AI vs. relationship-aware writing", href: "/demo?tab=article#demo-lab", external: false },
    { label: "AI Perception SDK", sub: "Live facial expression demo", href: "/demo?tab=perception#demo-lab", external: false },
    { label: "Emotion Profile Tracking", sub: "Sample multi-week pattern dashboard", href: "/demo/emotion-profile", external: false },
];

// Shared nav link style
const navLinkStyle = {
    fontFamily: "Manrope, sans-serif",
    fontSize: "13px",
    fontWeight: 500,
    color: "rgba(224,224,224,0.8)",
    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
    transition: "color 0.15s",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    display: "flex",
    alignItems: "center",
    gap: 4,
};

// Chevron icon
function Chevron({ open }) {
    return (
        <svg
            width="11"
            height="11"
            viewBox="0 0 12 12"
            fill="none"
            style={{
                transition: "transform 0.2s ease",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                opacity: 0.6,
                flexShrink: 0,
                marginTop: 1,
            }}
        >
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// Glass dropdown container
function DropdownMenu({ children, style }) {
    return (
        <div
            style={{
                position: "absolute",
                top: "calc(100% + 10px)",
                left: 0,
                minWidth: 220,
                background: "rgba(8, 8, 12, 0.88)",
                backdropFilter: "blur(24px)",
                WebkitBackdropFilter: "blur(24px)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 10,
                boxShadow: "0 16px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.03)",
                padding: "6px 0",
                zIndex: 200,
                ...style,
            }}
        >
            {children}
        </div>
    );
}

// Column inside the Products mega-dropdown
function ProductColumn({ title, items, onClose }) {
    const itemStyle = {
        display: "block",
        padding: "9px 12px",
        textDecoration: "none",
        transition: "background 0.12s",
        borderRadius: 6,
        cursor: "pointer",
    };

    const renderInner = (label, sub) => (
        <div>
            <div style={{
                fontFamily: "Manrope, sans-serif",
                fontSize: "13px",
                fontWeight: 500,
                color: "rgba(224,224,224,0.9)",
                marginBottom: 2,
            }}>
                {label}
            </div>
            <div style={{
                fontFamily: "Manrope, sans-serif",
                fontSize: "11px",
                color: "rgba(255,255,255,0.35)",
                letterSpacing: "0.02em",
            }}>
                {sub}
            </div>
        </div>
    );

    return (
        <div style={{ padding: "0 4px" }}>
            <div style={{
                fontFamily: "Manrope, sans-serif",
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.35)",
                padding: "4px 12px 10px",
            }}>
                {title}
            </div>
            {items.map(({ label, sub, href, external }) => (
                external ? (
                    <a
                        key={href}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={itemStyle}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
                        onMouseLeave={e => e.currentTarget.style.background = "none"}
                        onClick={onClose}
                    >
                        {renderInner(label, sub)}
                    </a>
                ) : (
                    <Link
                        key={href}
                        to={href}
                        style={itemStyle}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
                        onMouseLeave={e => e.currentTarget.style.background = "none"}
                        onClick={onClose}
                    >
                        {renderInner(label, sub)}
                    </Link>
                )
            ))}
        </div>
    );
}

export default function HeaderMenu() {
    const [industriesOpen, setIndustriesOpen] = useState(false);
    const [sdkOpen, setSdkOpen] = useState(false);
    const [productsOpen, setProductsOpen] = useState(false);
    const [useCasesOpen, setUseCasesOpen] = useState(false);
    const [demoOpen, setDemoOpen] = useState(false);
    const industriesMenuRef = useRef(null);
    const sdkMenuRef = useRef(null);
    const productsMenuRef = useRef(null);
    const useCasesMenuRef = useRef(null);
    const demoMenuRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const isOnHome = location.pathname === "/" || location.pathname === "/aihumanity";
    const isOnBlog = location.pathname.startsWith("/blog");
    const isOnDemo = location.pathname.startsWith("/demo");
    const isOnBenchmarks = location.pathname.startsWith("/benchmarks");

    useEffect(() => {
        function handleClickOutside(e) {
            if (industriesMenuRef.current && !industriesMenuRef.current.contains(e.target)) setIndustriesOpen(false);
            if (sdkMenuRef.current && !sdkMenuRef.current.contains(e.target)) setSdkOpen(false);
            if (productsMenuRef.current && !productsMenuRef.current.contains(e.target)) setProductsOpen(false);
            if (useCasesMenuRef.current && !useCasesMenuRef.current.contains(e.target)) setUseCasesOpen(false);
            if (demoMenuRef.current && !demoMenuRef.current.contains(e.target)) setDemoOpen(false);
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    function closeAll() {
        setIndustriesOpen(false);
        setSdkOpen(false);
        setProductsOpen(false);
        setUseCasesOpen(false);
        setDemoOpen(false);
    }

    function handleIndustryClick(anchor) {
        setIndustriesOpen(false);
        if (isOnHome) {
            const el = document.getElementById(anchor);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            navigate(`/#${anchor}`);
        }
    }

    return (
        <nav style={{ display: "flex", alignItems: "center", gap: 28 }}>

            {/* Industries dropdown (market verticals — anchors on home page) */}
            <div style={{ position: "relative" }} ref={industriesMenuRef}>
                <button
                    style={navLinkStyle}
                    onClick={() => { setIndustriesOpen(v => !v); setSdkOpen(false); setProductsOpen(false); setUseCasesOpen(false); setDemoOpen(false); }}
                    onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                    onMouseLeave={e => e.currentTarget.style.color = "rgba(224,224,224,0.8)"}
                >
                    Industries
                    <Chevron open={industriesOpen} />
                </button>

                {industriesOpen && (
                    <DropdownMenu>
                        <div style={{
                            fontFamily: "Manrope, sans-serif",
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.35)",
                            padding: "4px 16px 10px",
                        }}>
                            Where it fits
                        </div>
                        {INDUSTRIES.map(({ label, anchor }) => (
                            <button
                                key={anchor}
                                style={{
                                    width: "100%",
                                    textAlign: "left",
                                    padding: "10px 16px",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    fontFamily: "Manrope, sans-serif",
                                    fontSize: "13px",
                                    fontWeight: 400,
                                    color: "rgba(224,224,224,0.75)",
                                    transition: "background 0.12s, color 0.12s",
                                    borderRadius: 6,
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.color = "#fff"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "rgba(224,224,224,0.75)"; }}
                                onClick={() => handleIndustryClick(anchor)}
                            >
                                {label}
                            </button>
                        ))}
                    </DropdownMenu>
                )}
            </div>

            {/* Use Cases dropdown (benefit-driven, split B2C / B2B) */}
            <div style={{ position: "relative" }} ref={useCasesMenuRef}>
                <button
                    style={navLinkStyle}
                    onClick={() => { setUseCasesOpen(v => !v); setIndustriesOpen(false); setSdkOpen(false); setProductsOpen(false); setDemoOpen(false); }}
                    onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                    onMouseLeave={e => e.currentTarget.style.color = "rgba(224,224,224,0.8)"}
                >
                    Use Cases
                    <Chevron open={useCasesOpen} />
                </button>

                {useCasesOpen && (
                    <DropdownMenu style={{ minWidth: 540, padding: "16px 8px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <div style={{
                            gridColumn: "1 / -1",
                            fontFamily: "Manrope, sans-serif",
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.35)",
                            padding: "4px 12px 6px",
                        }}>
                            Outcomes by audience
                        </div>
                        <ProductColumn title="For Individuals" items={USE_CASES_B2C} onClose={closeAll} />
                        <ProductColumn title="For Businesses" items={USE_CASES_B2B} onClose={closeAll} />
                    </DropdownMenu>
                )}
            </div>

            {/* Products dropdown (real products, split B2C / B2B) */}
            <div style={{ position: "relative" }} ref={productsMenuRef}>
                <button
                    style={navLinkStyle}
                    onClick={() => { setProductsOpen(v => !v); setIndustriesOpen(false); setSdkOpen(false); setUseCasesOpen(false); setDemoOpen(false); }}
                    onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                    onMouseLeave={e => e.currentTarget.style.color = "rgba(224,224,224,0.8)"}
                >
                    Products
                    <Chevron open={productsOpen} />
                </button>

                {productsOpen && (
                    <DropdownMenu style={{ minWidth: 540, padding: "16px 8px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <ProductColumn title="For Consumers" items={PRODUCTS_B2C} onClose={closeAll} />
                        <ProductColumn title="For Developers & Enterprise" items={PRODUCTS_B2B} onClose={closeAll} />
                    </DropdownMenu>
                )}
            </div>

            {/* SDK & Platform dropdown */}
            <div style={{ position: "relative" }} ref={sdkMenuRef}>
                <button
                    style={navLinkStyle}
                    onClick={() => { setSdkOpen(v => !v); setIndustriesOpen(false); setProductsOpen(false); setUseCasesOpen(false); setDemoOpen(false); }}
                    onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                    onMouseLeave={e => e.currentTarget.style.color = "rgba(224,224,224,0.8)"}
                >
                    SDK & Platform
                    <Chevron open={sdkOpen} />
                </button>

                {sdkOpen && (
                    <DropdownMenu style={{ minWidth: 230 }}>
                        {SDK_LINKS.map(({ label, sub, href, external }) => {
                            const inner = (
                                <div>
                                    <div style={{
                                        fontFamily: "Manrope, sans-serif",
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        color: "rgba(224,224,224,0.9)",
                                        marginBottom: 2,
                                    }}>
                                        {label}
                                    </div>
                                    <div style={{
                                        fontFamily: "Manrope, sans-serif",
                                        fontSize: "11px",
                                        color: "rgba(255,255,255,0.35)",
                                        letterSpacing: "0.02em",
                                    }}>
                                        {sub}
                                    </div>
                                </div>
                            );

                            const sharedStyle = {
                                display: "block",
                                padding: "10px 16px",
                                textDecoration: "none",
                                transition: "background 0.12s",
                                borderRadius: 6,
                                cursor: "pointer",
                            };

                            return external ? (
                                <a
                                    key={href}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={sharedStyle}
                                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
                                    onMouseLeave={e => e.currentTarget.style.background = "none"}
                                    onClick={() => setSdkOpen(false)}
                                >
                                    {inner}
                                </a>
                            ) : (
                                <Link
                                    key={href}
                                    to={href}
                                    style={sharedStyle}
                                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
                                    onMouseLeave={e => e.currentTarget.style.background = "none"}
                                    onClick={() => setSdkOpen(false)}
                                >
                                    {inner}
                                </Link>
                            );
                        })}
                    </DropdownMenu>
                )}
            </div>

            {/* Benchmarks link */}
            <Link
                to="/benchmarks"
                style={{
                    ...navLinkStyle,
                    color: isOnBenchmarks ? "#fff" : "rgba(224,224,224,0.8)",
                    textDecoration: "none",
                    position: "relative",
                }}
                onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                onMouseLeave={e => e.currentTarget.style.color = isOnBenchmarks ? "#fff" : "rgba(224,224,224,0.8)"}
            >
                Benchmarks
                {isOnBenchmarks && (
                    <span style={{
                        position: "absolute",
                        bottom: -4,
                        left: 0,
                        right: 0,
                        height: 1.5,
                        background: "linear-gradient(90deg, #6ddcff, #7e60f8)",
                        borderRadius: 1,
                    }} />
                )}
            </Link>

            {/* Demo dropdown (real menu — starting points + per-product jump links) */}
            <div style={{ position: "relative" }} ref={demoMenuRef}>
                <button
                    style={{ ...navLinkStyle, color: isOnDemo ? "#fff" : "rgba(224,224,224,0.8)", position: "relative" }}
                    onClick={() => { setDemoOpen(v => !v); setIndustriesOpen(false); setSdkOpen(false); setProductsOpen(false); setUseCasesOpen(false); }}
                    onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                    onMouseLeave={e => e.currentTarget.style.color = isOnDemo ? "#fff" : "rgba(224,224,224,0.8)"}
                >
                    Demo
                    <Chevron open={demoOpen} />
                    {isOnDemo && (
                        <span style={{
                            position: "absolute",
                            bottom: -4,
                            left: 0,
                            right: 0,
                            height: 1.5,
                            background: "linear-gradient(90deg, #6ddcff, #7e60f8)",
                            borderRadius: 1,
                        }} />
                    )}
                </button>

                {demoOpen && (
                    <DropdownMenu style={{ minWidth: 540, padding: "16px 8px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <div style={{
                            gridColumn: "1 / -1",
                            fontFamily: "Manrope, sans-serif",
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.35)",
                            padding: "4px 12px 6px",
                        }}>
                            See it working
                        </div>
                        <ProductColumn title="Best Starting Demos" items={DEMO_STARTERS} onClose={closeAll} />
                        <ProductColumn title="By Product" items={DEMO_BY_PRODUCT} onClose={closeAll} />
                    </DropdownMenu>
                )}
            </div>

            {/* Blog link */}
            <Link
                to="/blog"
                style={{
                    ...navLinkStyle,
                    color: isOnBlog ? "#fff" : "rgba(224,224,224,0.8)",
                    textDecoration: "none",
                    position: "relative",
                }}
                onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                onMouseLeave={e => e.currentTarget.style.color = isOnBlog ? "#fff" : "rgba(224,224,224,0.8)"}
            >
                Blog
                {isOnBlog && (
                    <span style={{
                        position: "absolute",
                        bottom: -4,
                        left: 0,
                        right: 0,
                        height: 1.5,
                        background: "linear-gradient(90deg, #6ddcff, #7e60f8)",
                        borderRadius: 1,
                    }} />
                )}
            </Link>

        </nav>
    );
}
